export const MIN_DURATION_FACTOR = 0.3;
export const MAX_EFFECTIVE_DURATION_FACTOR = 3.0;
export const DUPLICATE_REGION_TOLERANCE = 0.015;

export const CURVE_SAMPLE_COUNT = 320;
export const MAX_VOICED_GAP_SECONDS = 0.08;
export const MIN_SMOOTH_SEGMENT_POINTS = 3;

export const MIN_VOICED_HZ = 55;
export const FLOOR_PITCH_EPSILON_HZ = 3;

/**
 * Minimum distance used by CurveEditingBlock when adding / dragging handles.
 * Exported here so the component can use one consistent rule.
 */
export const HANDLE_EDGE_GUARD_T = 0.018;
export const HANDLE_DUPLICATE_GUARD_T = 0.008;
export const HANDLE_DRAG_GAP_T = 0.01;

export function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function makeRegionId() {
  return `duration-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function makeHandleId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `handle-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getAudioFetchUrl(apiBaseUrl, audioUrl) {
  if (!audioUrl) return null;
  if (audioUrl.startsWith("http")) return encodeURI(audioUrl);
  return encodeURI(`${apiBaseUrl}${audioUrl}`);
}

export function extractPrivateAudioId(audioUrl) {
  if (!audioUrl) return null;

  const marker = "/audio/private/";
  const text = String(audioUrl);
  const index = text.indexOf(marker);

  if (index === -1) return null;

  return text.slice(index + marker.length).split(/[?#]/)[0];
}

export function extractHzValue(point) {
  if (typeof point === "number") return point;

  return (
    point?.hz ??
    point?.f0 ??
    point?.pitch ??
    point?.value ??
    point?.frequency ??
    point?.y ??
    null
  );
}

/**
 * Some pitch extractors return the pitch floor as fake "voiced" values.
 * We infer the lowest few returned values and suppress floor-like artifacts.
 */
export function inferReturnedPitchFloor(rawPitch = []) {
  const hzValues = [];

  if (!Array.isArray(rawPitch)) return null;

  for (const point of rawPitch) {
    const hz = Number(extractHzValue(point));

    if (Number.isFinite(hz) && hz > 0) {
      hzValues.push(hz);
    }
  }

  if (!hzValues.length) return null;

  hzValues.sort((a, b) => a - b);

  return hzValues[Math.floor(hzValues.length * 0.03)] || hzValues[0];
}

export function isUsablePitchHz(hz, inferredFloor = null) {
  const value = Number(hz);

  if (!Number.isFinite(value)) return false;
  if (value <= 0) return false;
  if (value < MIN_VOICED_HZ) return false;

  if (
    inferredFloor != null &&
    inferredFloor < MIN_VOICED_HZ + 8 &&
    value <= inferredFloor + FLOOR_PITCH_EPSILON_HZ
  ) {
    return false;
  }

  return true;
}

export function normalizePitchPoint(raw, index, total) {
  if (typeof raw === "number") {
    return {
      t: total > 1 ? index / (total - 1) : 0,
      hz: raw,
      time: undefined,
    };
  }

  const t =
    raw?.t ??
    raw?.x ??
    raw?.timeNorm ??
    raw?.time_normalized ??
    raw?.time ??
    (total > 1 ? index / (total - 1) : 0);

  const hz = extractHzValue(raw);

  return {
    t: clamp(safeNumber(t, 0), 0, 1),
    hz: Math.max(0, safeNumber(hz, 0)),
    time: Number.isFinite(Number(raw?.time)) ? Number(raw.time) : undefined,
  };
}

export function normalizePitchSeries(rawPitch = []) {
  if (!Array.isArray(rawPitch)) return [];

  const inferredFloor = inferReturnedPitchFloor(rawPitch);

  return rawPitch
    .map((point, index) => normalizePitchPoint(point, index, rawPitch.length))
    .filter((point) => isUsablePitchHz(point.hz, inferredFloor))
    .sort((a, b) => a.t - b.t);
}

export function normalizeBackendIntonationForEditor(intonation) {
  const intensity =
    intonation?.intensity ||
    intonation?.intonation?.intensity ||
    intonation?.intonation ||
    null;

  const xs = intensity?.pitch?.xs || [];
  const ys = intensity?.pitch?.ys || [];

  if (!Array.isArray(xs) || !Array.isArray(ys)) {
    return {
      points: [],
      start: 0,
      end: 1,
    };
  }

  const rawPitch = ys.map((hz, index) => ({
    time: xs[index],
    hz,
  }));

  const inferredFloor = inferReturnedPitchFloor(rawPitch);
  const pairs = [];

  for (let i = 0; i < Math.min(xs.length, ys.length); i += 1) {
    const time = Number(xs[i]);
    const hz = Number(ys[i]);

    if (!Number.isFinite(time)) continue;
    if (!isUsablePitchHz(hz, inferredFloor)) continue;

    pairs.push({
      time,
      hz,
    });
  }

  if (pairs.length < 2) {
    return {
      points: [],
      start: 0,
      end: 1,
    };
  }

  const startTime = pairs[0].time;
  const endTime = pairs[pairs.length - 1].time;
  const span = endTime - startTime || 1;

  return {
    start: startTime,
    end: endTime,
    points: pairs.map((point) => ({
      t: clamp((point.time - startTime) / span, 0, 1),
      time: point.time,
      hz: point.hz,
    })),
  };
}

export function getPitchStats(points) {
  const inferredFloor = inferReturnedPitchFloor(points);

  const voiced = (points || [])
    .map((p) => Number(p.hz))
    .filter((hz) => isUsablePitchHz(hz, inferredFloor));

  if (!voiced.length) {
    return {
      minHz: 75,
      maxHz: 350,
      centerHz: 180,
    };
  }

  const minHz = Math.min(...voiced);
  const maxHz = Math.max(...voiced);
  const centerHz = voiced.reduce((sum, hz) => sum + hz, 0) / voiced.length;

  return {
    minHz: Math.max(40, minHz - 35),
    maxHz: Math.min(650, Math.max(maxHz + 35, minHz + 90)),
    centerHz,
  };
}

export function pitchToSvgY(hz, height, minHz, maxHz) {
  const ratio = (hz - minHz) / (maxHz - minHz || 1);
  return height - clamp(ratio, 0, 1) * height;
}

export function svgYToPitch(y, height, minHz, maxHz) {
  const ratio = 1 - y / height;
  return minHz + clamp(ratio, 0, 1) * (maxHz - minHz);
}

export function getTimeFromT(start, end, t) {
  return Number(start) + Number(t) * (Number(end) - Number(start));
}

export function getTFromTime(start, end, time) {
  const span = Number(end) - Number(start) || 1;
  return clamp((Number(time) - Number(start)) / span, 0, 1);
}

export function makeTimelineX({ relativeTime, maxDuration, width }) {
  if (!Number.isFinite(relativeTime) || maxDuration <= 0) return 0;
  return clamp(relativeTime / maxDuration, 0, 1) * width;
}

export function getPointTime(point, curveStart, curveEnd) {
  if (Number.isFinite(Number(point?.time))) {
    return Number(point.time);
  }

  return Number(curveStart) + Number(point?.t) * (Number(curveEnd) - Number(curveStart));
}

export function buildTimedPitchPoints({ points, curveStart, curveEnd }) {
  const inferredFloor = inferReturnedPitchFloor(points);

  return (points || [])
    .map((point) => {
      const time = getPointTime(point, curveStart, curveEnd);

      return {
        ...point,
        time,
        hz: Number(point.hz),
      };
    })
    .filter(
      (point) =>
        Number.isFinite(point.time) &&
        isUsablePitchHz(point.hz, inferredFloor)
    )
    .sort((a, b) => a.time - b.time);
}

export function buildVoicedIntervalsFromPoints({
  points,
  curveStart,
  curveEnd,
  maxGapSeconds = MAX_VOICED_GAP_SECONDS,
}) {
  const timed = buildTimedPitchPoints({
    points,
    curveStart,
    curveEnd,
  });

  if (timed.length < 2) return [];

  const intervals = [];
  let startTime = timed[0].time;
  let previousTime = timed[0].time;

  for (let i = 1; i < timed.length; i += 1) {
    const currentTime = timed[i].time;
    const gap = currentTime - previousTime;

    if (gap > maxGapSeconds) {
      if (previousTime > startTime) {
        intervals.push({
          start: startTime,
          end: previousTime,
        });
      }

      startTime = currentTime;
    }

    previousTime = currentTime;
  }

  if (previousTime > startTime) {
    intervals.push({
      start: startTime,
      end: previousTime,
    });
  }

  return intervals;
}

export function isTimeInsideVoicedIntervals(time, intervals) {
  if (!Number.isFinite(Number(time))) return false;

  const value = Number(time);
  const pad = 0.002;

  return intervals.some(
    (interval) => value >= interval.start - pad && value <= interval.end + pad
  );
}

export function getHzAtAbsoluteTime({
  points,
  curveStart,
  curveEnd,
  absoluteTime,
  voicedIntervals,
}) {
  if (!points?.length) return null;

  if (!isTimeInsideVoicedIntervals(absoluteTime, voicedIntervals)) {
    return null;
  }

  const timed = buildTimedPitchPoints({
    points,
    curveStart,
    curveEnd,
  });

  if (timed.length < 2) return null;

  if (
    absoluteTime < timed[0].time ||
    absoluteTime > timed[timed.length - 1].time
  ) {
    return null;
  }

  for (let i = 0; i < timed.length - 1; i += 1) {
    const a = timed[i];
    const b = timed[i + 1];

    if (absoluteTime >= a.time && absoluteTime <= b.time) {
      const gap = b.time - a.time;

      // Never interpolate pitch through unvoiced/devoiced gaps.
      if (gap > MAX_VOICED_GAP_SECONDS) {
        return null;
      }

      const local = gap > 0 ? (absoluteTime - a.time) / gap : 0;

      return Number(a.hz) + (Number(b.hz) - Number(a.hz)) * local;
    }
  }

  return null;
}

export function densifyCurveByTime({
  points,
  curveStart,
  curveEnd,
  sampleCount = CURVE_SAMPLE_COUNT,

  // Voice mask decides where pitch is allowed to be drawn.
  // For edited target curves, pass basePoints here so handles cannot bridge gaps.
  voiceMaskPoints = points,
  voiceMaskStart = curveStart,
  voiceMaskEnd = curveEnd,
}) {
  if (!points?.length) return [];

  const duration = Math.max(0.001, Number(curveEnd) - Number(curveStart));
  const output = [];

  const voicedIntervals = buildVoicedIntervalsFromPoints({
    points: voiceMaskPoints,
    curveStart: voiceMaskStart,
    curveEnd: voiceMaskEnd,
    maxGapSeconds: MAX_VOICED_GAP_SECONDS,
  });

  for (let i = 0; i < sampleCount; i += 1) {
    const t = sampleCount > 1 ? i / (sampleCount - 1) : 0;
    const relativeTime = t * duration;
    const absoluteTime = Number(curveStart) + relativeTime;

    const hz = getHzAtAbsoluteTime({
      points,
      curveStart,
      curveEnd,
      absoluteTime,
      voicedIntervals,
    });

    if (!Number.isFinite(hz) || hz <= 0) {
      output.push({
        t,
        time: absoluteTime,
        hz: null,
        voiced: false,
      });
      continue;
    }

    output.push({
      t,
      time: absoluteTime,
      hz,
      voiced: true,
    });
  }

  return output;
}

export function splitVoicedSegments(svgPoints) {
  const segments = [];
  let current = [];

  for (const point of svgPoints) {
    if (!point.voiced || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      if (current.length) {
        segments.push(current);
        current = [];
      }

      continue;
    }

    const previous = current[current.length - 1];

    if (
      previous &&
      Math.abs(point.relativeTime - previous.relativeTime) >
        MAX_VOICED_GAP_SECONDS
    ) {
      segments.push(current);
      current = [];
    }

    current.push(point);
  }

  if (current.length) segments.push(current);

  return segments;
}

export function catmullRomSegmentToBezier(p0, p1, p2, p3) {
  const tension = 1;

  const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
  const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
  const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
  const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;

  return `C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(
    2
  )} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
}

export function smoothSvgSegmentToPath(segment) {
  if (!segment.length) return "";

  if (segment.length === 1) {
    const p = segment[0];
    return `M ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  }

  if (segment.length === 2) {
    const [a, b] = segment;

    return [
      `M ${a.x.toFixed(2)} ${a.y.toFixed(2)}`,
      `L ${b.x.toFixed(2)} ${b.y.toFixed(2)}`,
    ].join(" ");
  }

  let d = `M ${segment[0].x.toFixed(2)} ${segment[0].y.toFixed(2)}`;

  for (let i = 0; i < segment.length - 1; i += 1) {
    const p0 = segment[Math.max(0, i - 1)];
    const p1 = segment[i];
    const p2 = segment[i + 1];
    const p3 = segment[Math.min(segment.length - 1, i + 2)];

    d += ` ${catmullRomSegmentToBezier(p0, p1, p2, p3)}`;
  }

  return d;
}

export function makeSmoothPathByTime({
  points,
  curveStart,
  curveEnd,
  maxDuration,
  width,
  height,
  minHz,
  maxHz,
  sampleCount = CURVE_SAMPLE_COUNT,

  // UX rule: draw pitch only where the voice mask says there is real voiced material.
  // This prevents edited handles from visually bridging over unvoiced audio.
  voiceMaskPoints = points,
  voiceMaskStart = curveStart,
  voiceMaskEnd = curveEnd,
}) {
  const dense = densifyCurveByTime({
    points,
    curveStart,
    curveEnd,
    voiceMaskPoints,
    voiceMaskStart,
    voiceMaskEnd,
    sampleCount,
  });

  const svgPoints = dense.map((point) => {
    const relativeTime = Number(point.time) - Number(curveStart);

    return {
      ...point,
      relativeTime,
      x: makeTimelineX({
        relativeTime,
        maxDuration,
        width,
      }),
      y: point.voiced ? pitchToSvgY(point.hz, height, minHz, maxHz) : null,
    };
  });

  const segments = splitVoicedSegments(svgPoints).filter(
    (segment) => segment.length >= MIN_SMOOTH_SEGMENT_POINTS
  );

  return segments.map(smoothSvgSegmentToPath).join(" ");
}

export function getCurveHzAtT(points, t) {
  if (!points?.length) return null;

  const sorted = [...points].sort((a, b) => a.t - b.t);

  if (t <= sorted[0].t) return sorted[0].hz;

  if (t >= sorted[sorted.length - 1].t) {
    return sorted[sorted.length - 1].hz;
  }

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i];
    const b = sorted[i + 1];

    if (t >= a.t && t <= b.t) {
      const aTime = Number(a.time);
      const bTime = Number(b.time);

      if (
        Number.isFinite(aTime) &&
        Number.isFinite(bTime) &&
        bTime - aTime > MAX_VOICED_GAP_SECONDS
      ) {
        return null;
      }

      const span = b.t - a.t || 1;
      const local = (t - a.t) / span;

      return a.hz + (b.hz - a.hz) * local;
    }
  }

  return sorted[sorted.length - 1].hz;
}

export function getCurvePointAtT(points, t) {
  const hz = getCurveHzAtT(points, t);

  if (hz == null) return null;

  return {
    t,
    hz,
  };
}

export function makeHandlePoints(basePoints, count = 9) {
  if (!basePoints.length) {
    return [
      { id: makeHandleId(), t: 0, originalHz: 160, hz: 160 },
      { id: makeHandleId(), t: 0.2, originalHz: 170, hz: 170 },
      { id: makeHandleId(), t: 0.4, originalHz: 190, hz: 190 },
      { id: makeHandleId(), t: 0.6, originalHz: 185, hz: 185 },
      { id: makeHandleId(), t: 0.8, originalHz: 170, hz: 170 },
      { id: makeHandleId(), t: 1, originalHz: 160, hz: 160 },
    ];
  }

  if (basePoints.length <= count) {
    const direct = basePoints.map((p) => ({
      id: makeHandleId(),
      t: p.t,
      originalHz: p.hz,
      hz: p.hz,
      time: p.time,
    }));

    direct[0].t = 0;
    direct[direct.length - 1].t = 1;

    return direct;
  }

  const out = [];

  for (let i = 0; i < count; i += 1) {
    const rawIndex = (i / (count - 1)) * (basePoints.length - 1);
    const index = Math.round(rawIndex);
    const p = basePoints[index];

    out.push({
      id: makeHandleId(),
      t: p.t,
      originalHz: p.hz,
      hz: p.hz,
      time: p.time,
    });
  }

  out[0].t = 0;
  out[out.length - 1].t = 1;

  return out;
}

export function deltaOf(handle) {
  return safeNumber(handle.hz) - safeNumber(handle.originalHz);
}

export function catmullRom(y0, y1, y2, y3, t) {
  const t2 = t * t;
  const t3 = t2 * t;

  return (
    0.5 *
    (
      2 * y1 +
      (-y0 + y2) * t +
      (2 * y0 - 5 * y1 + 4 * y2 - y3) * t2 +
      (-y0 + 3 * y1 - 3 * y2 + y3) * t3
    )
  );
}

export function interpolateHandleDelta(handles, t) {
  if (!handles.length) return 0;

  const sorted = [...handles].sort((a, b) => a.t - b.t);

  if (t <= sorted[0].t) return deltaOf(sorted[0]);

  if (t >= sorted[sorted.length - 1].t) {
    return deltaOf(sorted[sorted.length - 1]);
  }

  let segmentIndex = 0;

  for (let i = 0; i < sorted.length - 1; i += 1) {
    if (t >= sorted[i].t && t <= sorted[i + 1].t) {
      segmentIndex = i;
      break;
    }
  }

  const p0 = sorted[Math.max(0, segmentIndex - 1)];
  const p1 = sorted[segmentIndex];
  const p2 = sorted[segmentIndex + 1];
  const p3 = sorted[Math.min(sorted.length - 1, segmentIndex + 2)];

  const span = p2.t - p1.t || 1;
  const local = clamp((t - p1.t) / span, 0, 1);

  const result = catmullRom(
    deltaOf(p0),
    deltaOf(p1),
    deltaOf(p2),
    deltaOf(p3),
    local
  );

  const localMin = Math.min(deltaOf(p1), deltaOf(p2));
  const localMax = Math.max(deltaOf(p1), deltaOf(p2));
  const margin = 12;

  return clamp(result, localMin - margin, localMax + margin);
}

export function mergeCurveWithHandles(curvePoints, handles, pitchFloor, pitchCeiling) {
  const merged = [...(curvePoints || [])];

  for (const handle of handles || []) {
    const t = clamp(Number(handle.t), 0, 1);
    const hz = clamp(Number(handle.hz), pitchFloor, pitchCeiling);

    if (!Number.isFinite(t) || !Number.isFinite(hz)) continue;

    merged.push({
      t,
      hz,
      time: handle.time,
      isHandlePoint: true,
    });
  }

  merged.sort((a, b) => a.t - b.t);

  const deduped = [];

  for (const point of merged) {
    const last = deduped[deduped.length - 1];

    if (last && Math.abs(last.t - point.t) < 0.00001) {
      if (point.isHandlePoint) {
        deduped[deduped.length - 1] = point;
      }
    } else {
      deduped.push(point);
    }
  }

  return deduped;
}

/**
 * Backend/payload curve.
 * Includes handle points so the backend receives the intended target curve.
 */
export function buildEditedCurve(basePoints, handles, pitchFloor, pitchCeiling) {
  let denseCurve;

  if (!basePoints.length) {
    denseCurve = handles.map((h) => ({
      t: h.t,
      hz: clamp(h.hz, pitchFloor, pitchCeiling),
      time: h.time,
    }));
  } else {
    denseCurve = basePoints.map((p) => {
      const delta = interpolateHandleDelta(handles, p.t);

      return {
        t: p.t,
        hz: clamp(p.hz + delta, pitchFloor, pitchCeiling),
        time: p.time,
      };
    });
  }

  return mergeCurveWithHandles(denseCurve, handles, pitchFloor, pitchCeiling);
}

/**
 * Visual-only edited curve.
 *
 * It uses handles as deformation controls, but it does NOT merge handle points
 * into the path. This is important: handles are not voiced samples. If handles
 * become path points, they can visually bridge unvoiced gaps.
 */
export function buildEditedCurveSamples(basePoints, handles, pitchFloor, pitchCeiling) {
  if (!basePoints.length) {
    return [];
  }

  return basePoints.map((point) => {
    const delta = interpolateHandleDelta(handles, point.t);

    return {
      t: point.t,
      hz: clamp(point.hz + delta, pitchFloor, pitchCeiling),
      time: point.time,
    };
  });
}

export function buildDurationRegionEdit({
  id,
  start,
  end,
  rangeStartT,
  rangeEndT,
  factor,
  ramp = 0.03,
}) {
  const startTime = getTimeFromT(start, end, rangeStartT);
  const endTime = getTimeFromT(start, end, rangeEndT);

  return {
    id,
    type: "duration_region",
    mode: "stretch",
    startT: Number(Number(rangeStartT).toFixed(5)),
    endT: Number(Number(rangeEndT).toFixed(5)),
    startTime: Number(Number(startTime).toFixed(5)),
    endTime: Number(Number(endTime).toFixed(5)),
    factor: Number(Number(factor).toFixed(3)),
    ramp,
    anchor: "left",
  };
}

export function getOverlapKind(a, b) {
  const aStart = Number(a.startT);
  const aEnd = Number(a.endT);
  const bStart = Number(b.startT);
  const bEnd = Number(b.endT);

  const overlaps = aStart < bEnd && bStart < aEnd;

  if (!overlaps) return "none";

  const aContainsB = aStart <= bStart && aEnd >= bEnd;
  const bContainsA = bStart <= aStart && bEnd >= aEnd;

  if (aContainsB || bContainsA) return "containment";

  return "partial";
}

export function isNearDuplicateRegion(a, b) {
  return (
    Math.abs(Number(a.startT) - Number(b.startT)) <=
      DUPLICATE_REGION_TOLERANCE &&
    Math.abs(Number(a.endT) - Number(b.endT)) <= DUPLICATE_REGION_TOLERANCE
  );
}

export function computeMaxEffectiveDurationFactor(regions) {
  if (!regions.length) return 1;

  const boundaries = [];

  for (const region of regions) {
    boundaries.push(Number(region.startT));
    boundaries.push(Number(region.endT));
  }

  const sorted = [...new Set(boundaries)]
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);

  if (sorted.length < 2) {
    return Math.max(...regions.map((region) => Number(region.factor) || 1));
  }

  let maxFactor = 1;

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const mid = (sorted[i] + sorted[i + 1]) / 2;
    let factor = 1;

    for (const region of regions) {
      if (mid >= Number(region.startT) && mid <= Number(region.endT)) {
        factor *= Number(region.factor) || 1;
      }
    }

    maxFactor = Math.max(maxFactor, factor);
  }

  return maxFactor;
}

export function validateNewDurationRegion(newRegion, existingRegions) {
  const newFactor = Number(newRegion.factor);

  if (!Number.isFinite(newFactor)) {
    return {
      ok: false,
      message: "Choose a valid duration factor.",
    };
  }

  if (newFactor < MIN_DURATION_FACTOR || newFactor > MAX_EFFECTIVE_DURATION_FACTOR) {
    return {
      ok: false,
      message: `Duration factor must be between ${MIN_DURATION_FACTOR}× and ${MAX_EFFECTIVE_DURATION_FACTOR}×.`,
    };
  }

  for (const region of existingRegions) {
    if (isNearDuplicateRegion(newRegion, region)) {
      return {
        ok: false,
        message:
          "This is almost the same as an existing duration edit. Remove the old one first, or move A/B to a different region.",
      };
    }

    const overlapKind = getOverlapKind(newRegion, region);

    if (overlapKind === "partial") {
      return {
        ok: false,
        message:
          "This partially overlaps an existing duration edit. Use a separate region, or make one region fully contain the other.",
      };
    }
  }

  const maxEffectiveFactor = computeMaxEffectiveDurationFactor([
    ...existingRegions,
    newRegion,
  ]);

  if (maxEffectiveFactor > MAX_EFFECTIVE_DURATION_FACTOR) {
    return {
      ok: false,
      message: `This would create an effective stretch of ${maxEffectiveFactor.toFixed(
        2
      )}×. Keep stacked duration edits at or below ${MAX_EFFECTIVE_DURATION_FACTOR}×.`,
    };
  }

  return {
    ok: true,
    message: "",
  };
}

export function buildPitchPayload({
  start,
  end,
  basePoints,
  editedPoints,
  handles,
  pitchFloor,
  pitchCeiling,
  rangeStartT,
  rangeEndT,
}) {
  const rangeStartTime = getTimeFromT(start, end, rangeStartT);
  const rangeEndTime = getTimeFromT(start, end, rangeEndT);

  return {
    type: "pitch_bezier",
    mode: "target_hz_curve",
    start: Number(Number(start).toFixed(5)),
    end: Number(Number(end).toFixed(5)),
    pitchFloor: Number(Number(pitchFloor).toFixed(3)),
    pitchCeiling: Number(Number(pitchCeiling).toFixed(3)),
    playRange: {
      startT: Number(Number(rangeStartT).toFixed(5)),
      endT: Number(Number(rangeEndT).toFixed(5)),
      startTime: Number(Number(rangeStartTime).toFixed(5)),
      endTime: Number(Number(rangeEndTime).toFixed(5)),
    },
    points: editedPoints.map((p) => ({
      t: Number(Number(p.t).toFixed(5)),
      hz: Number(Number(p.hz).toFixed(3)),
      time: Number(
        (Number(start) + Number(p.t) * (Number(end) - Number(start))).toFixed(5)
      ),
    })),
    originalPoints: basePoints.map((p) => ({
      t: Number(Number(p.t).toFixed(5)),
      hz: Number(Number(p.hz).toFixed(3)),
      time: Number.isFinite(Number(p.time))
        ? Number(Number(p.time).toFixed(5))
        : undefined,
    })),
    handles: handles.map((h) => ({
      id: h.id,
      t: Number(Number(h.t).toFixed(5)),
      originalHz: Number(Number(h.originalHz).toFixed(3)),
      hz: Number(Number(h.hz).toFixed(3)),
      deltaHz: Number((Number(h.hz) - Number(h.originalHz)).toFixed(3)),
      time: Number(
        (Number(start) + Number(h.t) * (Number(end) - Number(start))).toFixed(5)
      ),
    })),
  };
}